import {
  faCheck,
  faEllipsisH,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import autosize from "autosize";
import {
  addHours,
  addMilliseconds,
  addMinutes,
  isBefore,
  isSameDay,
  max,
  startOfDay,
} from "date-fns";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  CustomInput,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { TimesheetEntry } from "../../core/types";
import useLocalStorage from "../../hooks/use-local-storage";
import useToggle from "../../hooks/use-toggle";
import { formatDe } from "../../utils/date";
import styled from "./EntryDialog.styles";

interface Props {
  entry?: TimesheetEntry;
  entries?: TimesheetEntry[];
  isOpen: boolean;
  day: Date;
  onSubmit?: (start: Date, end: Date, description?: string) => void;
  onClose?: () => void;
  onRemove?: () => void;
}

const EntryDialog: React.FC<Props> = ({
  isOpen,
  onSubmit,
  onClose,
  day,
  entry,
  entries,
  onRemove,
}: Props) => {
  const [initialStartTime, initialEndTime] = entry
    ? [entry.start, addMilliseconds(entry.start, entry.duration)]
    : getInitialValues(entries!, day);

  const [startTime, setStartTime, clearStart] = useLocalStorage(
    `pac.timesheet.entry.start.${formatDe(day, "yyMMdd")}`,
    formatDe(initialStartTime, "HH:mm"),
    { disabled: !!entry }
  );

  const [endTime, setEndTime, clearEnd] = useLocalStorage(
    `pac.timesheet.entry.end.${formatDe(day, "yyMMdd")}`,
    formatDe(initialEndTime, "HH:mm"),
    { disabled: !!entry }
  );

  const [isNonWorkingDay, setIsNonWorkingDay] = useState(false);

  const [isClosing, toggleClosing] = useToggle(!isOpen);
  const [showMenu, toggleMenu] = useToggle(false);

  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (descriptionRef.current) {
      autosize(descriptionRef.current);
    }
  }, [descriptionRef.current]);

  const [description, setDescription, clearDescription] = useLocalStorage(
    `pac.timesheet.entry.description.${formatDe(day, "yyMMdd")}`,
    entry?.description ?? "",
    { disabled: !!entry }
  );

  const handleSubmit = (e: React.FormEvent) => {
    try {
      e.preventDefault();

      if (isNonWorkingDay) {
        onSubmit!(day, day);
        setIsNonWorkingDay(false);
        return;
      }

      if (startTime === null || endTime === null) {
        throw new Error(
          `start and end time must be set (${JSON.stringify({
            startTime,
            endTime,
          })})`
        );
      }

      const [startHours, startMinutes] = startTime
        .split(":")
        .map((t: string) => Number(t));
      const [endHours, endMinutes] = endTime!
        .split(":")
        .map((t: string) => Number(t));
      const startDate = addMinutes(addHours(day, startHours), startMinutes);
      let endDate = addMinutes(addHours(day, endHours), endMinutes);

      if (isBefore(endDate, startDate)) {
        endDate = addHours(endDate, 24);
      }

      onSubmit!(startDate, endDate, description);
    } finally {
      clearStart();
      clearEnd();
      clearDescription();
    }
  };

  const requestDelete = useCallback(() => {
    if (!entry) {
      toggleClosing();
      clearStart();
      clearEnd();
      clearDescription();
      return;
    }
    if (!confirm("Eintrag wirklich löschen?")) {
      return;
    }
    onRemove?.();
    toggleClosing();
  }, [onRemove]);

  return (
    <Modal
      isOpen={!isClosing}
      toggle={toggleClosing}
      onClosed={() => {
        onClose?.();
      }}
    >
      <Form onSubmit={handleSubmit}>
        <styled.ModalHeader>
          {formatDe(day, "dd.MM.yyyy")}
          {" - "}
          {entry ? "Eintrag bearbeiten" : "Neuer Eintrag"}
          <Dropdown isOpen={showMenu} toggle={toggleMenu}>
            <DropdownToggle outline size="sm">
              <FontAwesomeIcon icon={faEllipsisH} />
            </DropdownToggle>
            <DropdownMenu right>
              <DropdownItem onClick={requestDelete}>
                <FontAwesomeIcon icon={faTrash} />{" "}
                {entry ? "Löschen" : "Entwurf verwerfen"}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </styled.ModalHeader>
        <ModalBody>
          <FormGroup>
            <CustomInput
              id="nonWorkingDay"
              type="switch"
              checked={isNonWorkingDay}
              label="Kein Arbeitstag"
              onChange={(ev) => {
                setIsNonWorkingDay(ev.target.checked);
              }}
            />
          </FormGroup>
          <FormGroup>
            <Label for="startTime">Startzeit</Label>
            <Input
              type="time"
              name="startTime"
              id="startTime"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              disabled={isNonWorkingDay}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label for="endTime">Endzeit</Label>
            <Input
              type="time"
              name="endTime"
              id="endTime"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              disabled={isNonWorkingDay}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label for="description">Beschreibung (optional)</Label>
            <Input
              innerRef={descriptionRef as any}
              type="textarea"
              rows={1}
              name="description"
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Woran haben Sie gearbeitet?"
              disabled={isNonWorkingDay}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            disabled={!isNonWorkingDay && (startTime === "" || endTime === "")}
            type="submit"
            color="primary"
          >
            <FontAwesomeIcon icon={faCheck} /> Zeit erfassen
          </Button>
          <Button color="link" onClick={toggleClosing}>
            Abbrechen
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

function getInitialValues(entries: TimesheetEntry[], day: Date) {
  const entriesOfSelectedDate = entries!.filter((e) => isSameDay(e.start, day));

  const initialStartTime = entriesOfSelectedDate.length
    ? max(
        entriesOfSelectedDate.map((e) => addMilliseconds(e.start, e.duration))
      )
    : addHours(startOfDay(day), 7);
  const initialEndTime = isSameDay(new Date(), day)
    ? max([new Date(), addHours(initialStartTime, 1)])
    : addHours(initialStartTime, 1);

  return [initialStartTime, initialEndTime];
}

EntryDialog.defaultProps = {
  entries: [],
  onSubmit() {},
  onClose() {},
  isOpen: false,
};

export default EntryDialog;
